import { Link } from 'react-router-dom';

function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="container">
        <Link to="/" className="back-link">&larr; Back to Home</Link>
        <h1>Terms of Service</h1>
        <p className="last-updated">Last updated: March 26, 2026</p>

        <section>
          <h2>Agreement to Terms</h2>
          <p>
            By using 10 Thirty, you agree to these terms. If you don't agree, please don't use our service.
            We may update these terms from time to time.
          </p>
        </section>

        <section>
          <h2>What is 10 Thirty?</h2>
          <p>
            10 Thirty is a secure, consent-based communication platform with end-to-end encrypted
            messaging, voice calls, and video calls. Tunnels (conversations) expire after 10 minutes,
            and messages disappear 30 seconds after being read. Voice and video calls are peer-to-peer
            and never recorded.
          </p>
        </section>

        <section>
          <h2>Eligibility</h2>
          <ul>
            <li>You must be at least 16 years old to use 10 Thirty</li>
            <li>You must provide a valid phone number</li>
            <li>You may only create one account per phone number</li>
          </ul>
        </section>

        <section>
          <h2>Your Responsibilities</h2>
          <p>You agree to:</p>
          <ul>
            <li>Keep your account credentials secure</li>
            <li>Not use 10 Thirty for illegal activities</li>
            <li>Not harass, threaten, or harm other users</li>
            <li>Not attempt to circumvent our security measures</li>
            <li>Not use automated systems to access the service</li>
            <li>Not share illegal content including child exploitation material</li>
            <li>Verify contact identity using Safety Numbers when communicating sensitive information</li>
          </ul>
        </section>

        <section>
          <h2>How Tunnels Work</h2>
          <ul>
            <li>Tunnels require mutual consent — the recipient must accept your request</li>
            <li>When opening a tunnel, you choose the communication type: chat, voice call, or video call</li>
            <li>Tunnels automatically close after 10 minutes</li>
            <li>Messages are deleted 30 seconds after being read</li>
            <li>Once a tunnel closes or a message is deleted, it cannot be recovered</li>
            <li>We cannot retrieve deleted messages or call records under any circumstances</li>
          </ul>
        </section>

        <section>
          <h2>Voice and Video Calls</h2>
          <ul>
            <li>Calls are established peer-to-peer using WebRTC with DTLS-SRTP encryption</li>
            <li>Audio and video data flows directly between devices and never passes through our servers</li>
            <li>Calls are not recorded by 10 Thirty</li>
            <li>Calls end when the tunnel expires (maximum 10 minutes)</li>
            <li>You are responsible for your own environment when making video calls</li>
          </ul>
        </section>

        <section>
          <h2>Privacy and Encryption</h2>
          <p>
            10 Thirty uses end-to-end encryption via the Signal Protocol. This means:
          </p>
          <ul>
            <li>We cannot read your messages</li>
            <li>We cannot listen to or watch your calls</li>
            <li>We cannot recover deleted messages</li>
            <li>We cannot provide message content or call recordings to anyone, including law enforcement</li>
            <li>You are responsible for any content you send</li>
          </ul>
          <p>
            You can verify your contact's identity at any time using Safety Numbers. If a contact's
            security code changes, you will see a warning. We recommend verifying Safety Numbers
            through a separate trusted channel before sharing sensitive information.
          </p>
        </section>

        <section>
          <h2>Regulatory Compliance</h2>
          <p>
            <strong>Important:</strong> 10 Thirty is designed for privacy and does not retain
            message or call records. Users are solely responsible for ensuring their use of 10 Thirty
            complies with applicable laws and industry regulations, including but not limited to:
          </p>
          <ul>
            <li>Financial services record-keeping requirements (SEC, FINRA, FCA, FSCA)</li>
            <li>Healthcare communication regulations (HIPAA, NHA)</li>
            <li>Legal discovery and litigation hold obligations</li>
            <li>Employment and workplace communication policies</li>
            <li>POPIA data subject access requests (we can only provide account data, not message content)</li>
          </ul>
          <p>
            10 Thirty is not suitable as a primary communication tool for industries
            requiring message archival or audit trails. If your profession or industry
            mandates retention of business communications, you must use compliant
            alternatives for those conversations.
          </p>
        </section>

        <section>
          <h2>Subscription and Payments</h2>
          <ul>
            <li>10 Thirty offers free and paid tiers</li>
            <li>Paid subscriptions are billed monthly</li>
            <li>Payments are processed by Paystack in South African Rand (ZAR)</li>
            <li>You can cancel your subscription at any time from the app or web profile</li>
            <li>Refunds are handled in accordance with South African consumer protection law</li>
          </ul>
        </section>

        <section>
          <h2>Account Termination</h2>
          <p>
            We may suspend or terminate your account if you violate these terms.
            You can delete your account at any time from within the app. Account deletion
            permanently removes all your data from our servers and destroys all local
            encryption keys, making cached data unrecoverable.
          </p>
        </section>

        <section>
          <h2>Disclaimers</h2>
          <ul>
            <li>10 Thirty is provided "as is" without warranties</li>
            <li>We don't guarantee 100% uptime or availability</li>
            <li>We're not responsible for content sent between users</li>
            <li>We're not liable for any damages arising from use of the service</li>
            <li>We're not liable for any regulatory non-compliance resulting from your use of the service</li>
            <li>You assume all risk for using ephemeral messaging in professional contexts</li>
            <li>Call quality depends on network conditions and is not guaranteed</li>
          </ul>
        </section>

        <section>
          <h2>Governing Law</h2>
          <p>
            These terms are governed by the laws of South Africa, including the Protection
            of Personal Information Act (POPIA), the Consumer Protection Act, and the
            Electronic Communications and Transactions Act. Any disputes will be resolved
            in the courts of South Africa.
          </p>
        </section>

        <section>
          <h2>Contact</h2>
          <p>
            Questions about these terms? Contact us at{' '}
            <a href="mailto:legal@10thirty.co.za">legal@10thirty.co.za</a>
          </p>
        </section>

        <footer className="legal-footer">
          <div className="links">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default TermsOfService;
